import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import { getAuth } from "firebase/auth"
import fbConf from "../FirebaseConf"
import Comm from "../Comm"
import Modal from "../components/Modal"
import Button from "../components/Button"
import Checkbox from "../components/Checkbox"
import WhiteSpace from "../components/Whitespace"
import LoaderDots from "../components/LoaderDots"
import Icon from "../Icon"
import ChargerType from "../types/ChargerType"
import { BREAKPOINTS, TAP_MIN } from "../GlobalTheme"
import { useConfirm } from "../ConfirmProvider"
import { useNotification } from "./Notification"

/** One stored config from voltie_financial.overuse (DailyOverUsageDTO). */
export interface OveruseConfig {
  id?: string
  userId: string
  startTime: string
  endTime: string
  connectionTime: number
  idleTime: number
  chargeTime: number
  chargerIds: string[]
}

const Container = styled.div`
  padding: 24px;
  width: min(560px, calc(100vw - 72px));
  box-sizing: border-box;
`

const Title = styled.p`
  font-weight: 700;
  font-size: 16px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
`

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: ${props => props.theme.colors.gray_600};
`

const Hint = styled.p`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.theme.colors.gray_500};
  margin: 0px;
`

const TimeInput = styled.input`
  font-family: inherit;
  font-size: 14px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.gray_300};
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.gray_900};
  box-sizing: border-box;
  width: 100%;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    min-height: ${TAP_MIN}px;
  }
`

const Row = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    flex-wrap: wrap;
  }
`

const Col = styled.div`
  flex: 1 1 0;
  min-width: 140px;
`

/* The picker has to cope with an account that owns hundreds of chargers, so it searches and scrolls
   rather than growing the modal. */
const ChargerList = styled.div`
  border: 1px solid ${props => props.theme.colors.gray_300};
  border-radius: 8px;
  max-height: 220px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 8px;
`

const ChargerRow = styled.div`
  padding: 2px 0px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    min-height: ${TAP_MIN}px;
    display: flex;
    align-items: center;
  }
`

const ConfigRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${props => props.theme.colors.gray_300};
  border-radius: 8px;
  margin-bottom: 8px;
`

const ConfigSummary = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`

const ConfigMain = styled.p`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
`

const ConfigSub = styled.p`
  font-weight: 500;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_500};
  margin: 0px;
  margin-top: 2px;
  overflow-wrap: anywhere;
`

const IconButton = styled.div`
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex: 0 0 auto;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: ${TAP_MIN}px;
    height: ${TAP_MIN}px;
    display: flex;
    align-items: center;
    justify-content: center;

    > svg {
      width: 18px;
      height: 18px;
    }
  }
`

const ButtonRow = styled.div`
  display: flex;
  margin-top: 20px;
`

const SearchInput = styled.input`
  font-family: inherit;
  font-size: 13px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.gray_300};
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.gray_900};
  box-sizing: border-box;
  width: 100%;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    min-height: ${TAP_MIN}px;
  }
`

/* The API stores the threshold in seconds; nobody configures a work-day limit in seconds, so the
   field is minutes and converts on the way in and out. */
const secondsToMinutes = (seconds: number) => Math.max(1, Math.round((seconds || 0) / 60))
const minutesToSeconds = (minutes: number) => Math.round(minutes * 60)

/* An admin's Chargers page holds every charger in the fleet (~2700), and rendering a checkbox for
   each one makes the modal crawl. The list shows a page of matches and asks for a narrower search. */
const MAX_VISIBLE_CHARGERS = 200

const emptyConfig = (userId: string): OveruseConfig => ({
  userId, startTime: '08:00', endTime: '17:00', connectionTime: 3600, idleTime: 0, chargeTime: 0, chargerIds: [],
})

export default function OveruseModal({ chargers, onClose }: { chargers?: ChargerType[], onClose: () => void }) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const { addNotification } = useNotification()

  const [configs, setConfigs] = useState<OveruseConfig[] | null>(null)
  const [editing, setEditing] = useState<OveruseConfig | null>(null)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')

  const uid = getAuth(fbConf).currentUser?.uid || ''

  async function fetchConfigs() {
    setConfigs(null)
    try {
      const response = await Comm('/api-cdr/overuse/user', { headers: { user_id: uid } })
      // 204 is the documented "this user has no config yet" answer, and has no body to parse.
      if (response.status === 204) { setConfigs([]); return }
      if (response.status !== 200) { setConfigs([]); return }
      const body = await response.json()
      setConfigs(Array.isArray(body) ? body : [])
    } catch {
      setConfigs([])
    }
  }

  useEffect(() => {
    fetchConfigs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleSave() {
    if (!editing) return
    if (!editing.startTime || !editing.endTime) return
    setIsSaving(true)
    try {
      const body = JSON.stringify({
        userId: uid,
        startTime: editing.startTime,
        endTime: editing.endTime,
        connectionTime: editing.connectionTime,
        idleTime: editing.idleTime || 0,
        chargeTime: editing.chargeTime || 0,
        chargerIds: editing.chargerIds,
      })
      const response = editing.id
        ? await Comm('/api-cdr/overuse/update', { method: 'PUT', headers: { object_id: editing.id, 'Content-Type': 'application/json' }, body })
        : await Comm('/api-cdr/overuse/create', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body })
      if (response.status === 200) {
        setEditing(null)
        await fetchConfigs()
      } else if (response.status === 400) {
        addNotification(t('chargers_invalidTimeWindow'), 'TEXT', 'error')
      }
    } catch { /* Comm already surfaced it */ }
    setIsSaving(false)
  }

  async function handleDelete(config: OveruseConfig) {
    if (!config.id) return
    if (!(await confirm({ message: t('chargers_areYouSureYouWantToDeleteThisOveruseMonitoring'), showCancel: true }))) return
    try {
      const response = await Comm('/api-cdr/overuse/delete', { method: 'DELETE', headers: { object_id: config.id } })
      if (response.status === 200) await fetchConfigs()
    } catch { /* Comm already surfaced it */ }
  }

  const matchingChargers = (chargers || []).filter(charger => {
    if (!search.trim()) return true
    const needle = search.trim().toLowerCase()
    return (charger.chargerId || '').toLowerCase().includes(needle)
      || (charger.localName || '').toLowerCase().includes(needle)
  })
  const visibleChargers = matchingChargers.slice(0, MAX_VISIBLE_CHARGERS)

  function toggleCharger(chargerId: string) {
    if (!editing) return
    setEditing({
      ...editing,
      chargerIds: editing.chargerIds.includes(chargerId)
        ? editing.chargerIds.filter(id => id !== chargerId)
        : [...editing.chargerIds, chargerId],
    })
  }

  function describeChargers(config: OveruseConfig) {
    if (!config.chargerIds || config.chargerIds.length === 0) return t('chargers_allChargers')
    return config.chargerIds
      .map(id => (chargers || []).find(charger => charger.chargerId === id)?.localName || id)
      .join(', ')
  }

  return (
    <Modal onEscape={onClose}>
      <Container>
        <Title>{t('chargers_overuseMonitoring')}</Title>
        <WhiteSpace height="4px" />
        <Hint>{t('chargers_getNotifiedWhenAChargerIsOccupiedLongerThanAllowed')}</Hint>
        <WhiteSpace height="16px" />

        {editing ? (
          <>
            <Row>
              <Col>
                <FieldLabel>{t('chargers_from')}</FieldLabel>
                <WhiteSpace height="4px" />
                <TimeInput type="time" value={editing.startTime} onChange={e => setEditing({ ...editing, startTime: e.target.value })} />
              </Col>
              <Col>
                <FieldLabel>{t('chargers_to')}</FieldLabel>
                <WhiteSpace height="4px" />
                <TimeInput type="time" value={editing.endTime} onChange={e => setEditing({ ...editing, endTime: e.target.value })} />
              </Col>
              <Col>
                <FieldLabel>{t('chargers_maxConnectionTimeMinutes')}</FieldLabel>
                <WhiteSpace height="4px" />
                <TimeInput type="number" min={1} value={secondsToMinutes(editing.connectionTime)}
                  onChange={e => setEditing({ ...editing, connectionTime: minutesToSeconds(parseInt(e.target.value) || 1) })} />
              </Col>
            </Row>

            <WhiteSpace height="16px" />
            <FieldLabel>{t('chargers_monitoredChargers')}</FieldLabel>
            <WhiteSpace height="4px" />
            <Hint>{t('chargers_ifYouSelectNoneAllChargersAreMonitored')}</Hint>
            <WhiteSpace height="8px" />
            <SearchInput placeholder={t('chargers_search')} value={search} onChange={e => setSearch(e.target.value)} />
            <WhiteSpace height="8px" />
            <ChargerList>
              {visibleChargers.map(charger => (
                <ChargerRow key={charger.chargerId}>
                  <Checkbox
                    label={charger.localName ? `${charger.localName} (${charger.chargerId})` : charger.chargerId}
                    value={editing.chargerIds.includes(charger.chargerId)}
                    onClick={() => toggleCharger(charger.chargerId)} />
                </ChargerRow>
              ))}
              {matchingChargers.length > visibleChargers.length && (
                <Hint>{t('chargers_refineYourSearchToSeeMoreChargers')}</Hint>
              )}
            </ChargerList>
            <WhiteSpace height="8px" />
            <Row>
              <Button label={t('chargers_selectAll')} width="calc(50% - 6px)" bgcolor="transparent" labelcolor="gray_900"
                onclick={() => setEditing({ ...editing, chargerIds: [...new Set([...editing.chargerIds, ...matchingChargers.map(c => c.chargerId)])] })} />
              <Button label={t('chargers_clearSelection')} width="calc(50% - 6px)" bgcolor="transparent" labelcolor="gray_900"
                onclick={() => setEditing({ ...editing, chargerIds: [] })} />
            </Row>

            <ButtonRow>
              <Button label={t('chargers_cancel')} width="calc(50% - 8px)" bgcolor="transparent" labelcolor="gray_900" onclick={() => setEditing(null)} />
              <WhiteSpace width="16px" />
              <Button label={isSaving ? '...' : t('chargers_save')} width="calc(50% - 8px)" onclick={isSaving ? () => { } : handleSave} />
            </ButtonRow>
          </>
        ) : configs === null ? (
          <p style={{ textAlign: 'center' }}><LoaderDots /></p>
        ) : (
          <>
            {configs.length === 0 && <Hint>{t('chargers_noOveruseMonitoringSetUpYet')}</Hint>}
            {configs.map(config => (
              <ConfigRow key={config.id}>
                <ConfigSummary>
                  <ConfigMain>{`${config.startTime} - ${config.endTime}  ·  ${secondsToMinutes(config.connectionTime)} ${t('chargers_min')}`}</ConfigMain>
                  <ConfigSub>{describeChargers(config)}</ConfigSub>
                </ConfigSummary>
                <IconButton title={t('charger_edit')} onClick={() => setEditing({ ...config, chargerIds: config.chargerIds || [] })}>
                  <Icon path="general.edit" width="100%" height="100%" />
                </IconButton>
                <IconButton title={t('charger_delete')} onClick={() => handleDelete(config)}>
                  <Icon path="general.trash" width="100%" height="100%" />
                </IconButton>
              </ConfigRow>
            ))}
            <ButtonRow>
              <Button label={t('chargers_cancel')} width="calc(50% - 8px)" bgcolor="transparent" labelcolor="gray_900" onclick={onClose} />
              <WhiteSpace width="16px" />
              <Button label={t('chargers_add')} width="calc(50% - 8px)" onclick={() => { setSearch(''); setEditing(emptyConfig(uid)) }} />
            </ButtonRow>
          </>
        )}
      </Container>
    </Modal>
  )
}
